import { Link, useNavigate } from "react-router-dom";
import { Icon } from "@/components/primitives";
import { MobileFrame } from "@/components/MobileFrame";
import { VisionAccessibilitySections } from "@/components/VisionAccessibilitySections";
import { ReadingStyleSection } from "@/components/ReadingStyleSection";
import { useAdminRole } from "@/hooks/useAdminRole";

// ─────────────────────────────────────────────────────────────────────────────
// SettingsAccessibilityHub — central accessibility tab.
// Surfaces the same vision controls available in Theme settings, plus
// reading style, and high-level guidance for motor / cognitive / screen
// reader support already applied globally (focus rings, reduced motion,
// 44px tap targets, sticky actions, sr-only text). The detailed
// per-theme contrast report lives at /settings/accessibility.
// ─────────────────────────────────────────────────────────────────────────────

export default function SettingsAccessibilityHub() {
  const nav = useNavigate();
  const { isAdmin } = useAdminRole();

  const goBack = () => {
    if (window.history.length > 1) nav(-1);
    else nav("/settings");
  };

  return (
    <MobileFrame>
      <div className="paper-bg" style={{ minHeight: "100%", position: "relative" }}>
        {/* Header */}
        <div style={{
          padding: "18px 20px 12px",
          borderBottom: "1px dashed hsl(var(--sand-deep))",
          display: "flex", alignItems: "center", gap: 10,
          position: "relative", zIndex: 2,
        }}>
          <button
            onClick={goBack}
            aria-label="Back to settings"
            className="tap-target"
            style={{
              background: "none", border: "1px solid hsl(var(--ink))",
              padding: 6, cursor: "pointer", color: "hsl(var(--ink))",
            }}
          >
            {Icon.chev(16, "l")}
          </button>
          <div style={{ flex: 1 }}>
            <div className="caps" style={{ color: "hsl(var(--rust))" }}>Settings</div>
            <h1 style={{
              fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800, fontSize: 26,
              color: "hsl(var(--ink))", margin: "2px 0 0", lineHeight: 1.1,
            }}>
              Accessibility
            </h1>
          </div>
        </div>

        <div style={{ padding: "16px 20px 32px", position: "relative", zIndex: 2 }}>
          <p style={{
            fontFamily: "'Fraunces', 'Playfair Display', serif",
            fontSize: 13, color: "hsl(var(--ink-2))", lineHeight: 1.5,
            margin: "0 0 18px",
          }}>
            Tune Fig to your eyes, hands, and reading style. These preferences
            apply across the whole app and stay with your account.
          </p>

          {/* ── Vision ─────────────────────────────────────────────── */}
          <Section title="Vision">
            <VisionAccessibilitySections />
            {/* Contrast report is a dev/QA tool — admin-only. */}
            {isAdmin === true && (
              <div style={{ marginTop: 14 }}>
                <Link
                  to="/settings/accessibility"
                  className="tap-target"
                  style={{
                    display: "inline-flex",
                    background: "transparent",
                    color: "hsl(var(--ink))",
                    border: "1px solid hsl(var(--ink))",
                    padding: "8px 12px",
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase",
                    textDecoration: "none",
                  }}
                >
                  Open contrast report →
                </Link>
              </div>
            )}
          </Section>

          {/* ── Reading ────────────────────────────────────────────── */}
          <Section title="Reading">
            <ReadingStyleSection />
          </Section>

          {/* ── Motor ──────────────────────────────────────────────── */}
          <Section title="Motor">
            <Bullets
              items={[
                "Every tappable element meets the 44 × 44 px minimum hit area.",
                "Visible keyboard focus rings on every interactive element — never disabled.",
                "No gesture-only actions: every swipe-to-action also has a tap button.",
                "Primary actions (Verify, Add to closet) stay anchored to the bottom of the screen.",
              ]}
            />
          </Section>

          {/* ── Cognitive ──────────────────────────────────────────── */}
          <Section title="Cognitive">
            <Bullets
              items={[
                "Bottom navigation order never changes: Home · Feed · Camera · Grails · You.",
                "Multi-step flows show a Step X of Y indicator at the top.",
                "Errors say what happened and what to do next — no generic “Something went wrong”.",
                "No system interruptions during photo upload, verification, or form submission.",
              ]}
            />
          </Section>

          {/* ── Screen reader ──────────────────────────────────────── */}
          <Section title="Screen reader & motion">
            <Bullets
              items={[
                "Semantic HTML throughout — buttons are buttons, headings stack in order.",
                "Icon-only buttons carry descriptive aria-labels (camera, report, close, approve).",
                "Verification result, scan count, and moderation updates announce via aria-live.",
                "Respects your device’s “Reduce motion” setting — all animations disable automatically.",
              ]}
            />
          </Section>
        </div>
      </div>
    </MobileFrame>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <div className="caps" style={{
        color: "hsl(var(--rust))", marginBottom: 8,
        borderBottom: "1px solid hsl(var(--ink))", paddingBottom: 4,
      }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function Bullets({ items }: { items: string[] }) {
  return (
    <ul style={{
      listStyle: "none",
      padding: 0,
      margin: 0,
      display: "flex",
      flexDirection: "column",
      gap: 8,
    }}>
      {items.map((it, i) => (
        <li
          key={i}
          style={{
            display: "flex",
            gap: 8,
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 12,
            color: "hsl(var(--ink))",
            lineHeight: 1.5,
          }}
        >
          <span aria-hidden="true" style={{ color: "hsl(var(--rust))" }}>✦</span>
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}